/**
 * Nudge Component - iOS "Slide To Unlock" style sliding notification
 * Slides horizontally with the orb overlapping the right edge
 * 
 * Design: Fully rounded pill with orb sitting on top of right edge
 */

import React, { useState, useEffect } from 'react';

// Exit animation duration - must match CSS transition duration
const EXIT_ANIMATION_MS = 400;

interface NudgeProps {
  text: string;
  isVisible: boolean;
  onClick?: () => void;
  autoHideDelay?: number; // ms, 0 = never auto-hide
  onAutoHide?: () => void;
  orbSize?: number; // Size of the orb to match height
  /**
   * Which edge of the launcher the nudge slides out from (FLO-585).
   *   'right' — launcher overlaps the nudge's right edge, nudge extends left
   *            (the default bottom-right corner).
   *   'left'  — launcher overlaps the nudge's left edge, nudge extends right
   *            (bottom-left corner).
   */
  overlap?: 'right' | 'left';
  /**
   * The launcher's rendered width (px), so the overlap padding clears the entire
   * pill rather than just the old orb's diameter (FLO-585). Defaults to orbSize.
   */
  launcherWidth?: number;
}

export const Nudge: React.FC<NudgeProps> = ({
  text,
  isVisible,
  onClick,
  autoHideDelay = 0,
  onAutoHide,
  orbSize = 56,
  overlap = 'right',
  launcherWidth,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Handle visibility changes with animation
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Small delay to trigger CSS animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // Wait for exit animation to complete (matches CSS transition duration)
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, EXIT_ANIMATION_MS);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Auto-hide after delay
  useEffect(() => {
    if (isVisible && autoHideDelay > 0 && onAutoHide) {
      const timer = setTimeout(onAutoHide, autoHideDelay);
      return () => clearTimeout(timer);
    }
  }, [isVisible, autoHideDelay, onAutoHide]);

  if (!shouldRender) return null;

  const overlayPx = launcherWidth ?? orbSize; // full pill width, not the old orbSize/2

  return (
    <div
      onClick={onClick}
      {...(onClick && {
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault(); // Prevent page scroll on Space
            onClick();
          }
        },
      })}
      style={{
        position: 'absolute',
        ...(overlap === 'left'
          ? {
              left: 0,
              paddingLeft: `${overlayPx}px`,
              paddingRight: '24px',
            }
          : {
              right: 0,
              paddingLeft: '24px',
              paddingRight: `${overlayPx}px`,
            }),
        height: `${orbSize}px`,
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)',
        borderRadius: `${orbSize / 2}px`,
        boxShadow: `
          0 2px 20px rgba(0, 0, 0, 0.1),
          0 8px 32px rgba(0, 0, 0, 0.08),
          inset 0 1px 0 rgba(255, 255, 255, 1),
          inset 0 -1px 0 rgba(0, 0, 0, 0.02)
        `,
        cursor: onClick ? 'pointer' : 'default',
        opacity: isAnimating ? 1 : 0,
        transform: `translateX(${isAnimating ? '0' : overlap === 'left' ? '-30px' : '30px'})`,
        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
        pointerEvents: 'all',
        whiteSpace: 'nowrap',
        zIndex: 0,
      }}
    >
      {/* Main text */}
      <span
        style={{
          fontSize: '15px',
          fontWeight: '500',
          color: 'rgba(0, 0, 0, 0.85)',
          letterSpacing: '-0.01em',
          paddingRight: '24px',
        }}
      >
        {text}
      </span>
    </div>
  );
};

export default Nudge;
